import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from "firebase/auth";
import { resolveFirebaseConfig } from "@/features/auth/firebase/config";
import { messageForSignInError } from "@/features/auth/firebase/errors";

const UNAVAILABLE = "Sign-in is temporarily unavailable. Try again later.";

const EMAIL_STORAGE_KEY = "memoru_email_for_sign_in";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export function getFirebaseAuth(): Auth | null {
  const config = resolveFirebaseConfig();
  if (!config) return null;
  if (!app) app = initializeApp(config);
  if (!auth) auth = getAuth(app);
  return auth;
}

export async function signInWithGoogle(): Promise<User> {
  const current = getFirebaseAuth();
  if (!current) throw new Error(UNAVAILABLE);
  try {
    const result = await signInWithPopup(current, new GoogleAuthProvider());
    return result.user;
  } catch (error) {
    throw new Error(messageForSignInError(error, "Google sign-in failed. Try again."));
  }
}

export async function sendEmailSignInLink(email: string, continueUrl: string): Promise<void> {
  const current = getFirebaseAuth();
  if (!current) throw new Error(UNAVAILABLE);
  try {
    await sendSignInLinkToEmail(current, email, {
      url: continueUrl,
      handleCodeInApp: true,
    });
  } catch (error) {
    throw new Error(
      messageForSignInError(error, "Could not send a sign-in email right now. Try again later."),
    );
  }
  try {
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  } catch {
    // Email is still required to complete the link if storage is unavailable.
  }
}

export function readStoredSignInEmail(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearStoredSignInEmail() {
  try {
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function hrefIsEmailSignInLink(href: string): boolean {
  const current = getFirebaseAuth();
  if (!current) return false;
  return isSignInWithEmailLink(current, href);
}

export async function completeEmailSignIn(email: string, href: string): Promise<User> {
  const current = getFirebaseAuth();
  if (!current) throw new Error(UNAVAILABLE);
  try {
    const result = await signInWithEmailLink(current, email, href);
    return result.user;
  } catch (error) {
    throw new Error(
      messageForSignInError(error, "We couldn't complete sign-in. Request a new magic link or try again in a moment."),
    );
  }
}

export async function getFirebaseIdToken(forceRefresh = false): Promise<string | null> {
  const current = getFirebaseAuth();
  const user = current?.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export async function signOutFirebase(): Promise<void> {
  const current = getFirebaseAuth();
  if (!current) return;
  await firebaseSignOut(current);
}

export type { User as FirebaseUser };
